import { requestCodexText } from './codexText';
/**
 * Codex AI Prompt Enhancement Service
 *
 * Uses the private Codex subscription to intelligently enhance user prompts for character generation:
 * - Sanitizes NSFW terms (e.g., "breasts" → "chest", "nipples" → "torso")
 * - Converts natural language to JSON-structured prompts optimized for Sunburst
 * - Applies model-sympathetic language that gets better results
 * - Maintains artistic intent while ensuring safety and quality
 *
 * Integrated with existing character layer system (body, face, hair, clothing, accessories)
 */

import { CharacterForm } from '../types';

// ============================================
// TYPES
// ============================================

export interface GeminiEnhancementRequest {
  userPrompt: string;
  layerType: 'body' | 'head' | 'face' | 'hair' | 'clothing' | 'accessories';
  form: CharacterForm;
  stylePreset?: string;
  archetype?: string;
  hasInputImage?: boolean;
  gender?: 'male' | 'female' | 'neutral';
  // Body layer context for head generation - enforces consistency
  bodyEnhancedPrompt?: string; // The enhanced prompt from body layer
  bodyEthnicity?: string; // Explicitly extracted ethnicity from body layer
  bodySkinTone?: string; // Explicitly extracted skin tone from body layer
  enforceBodyConsistency?: boolean; // When true, head MUST match body characteristics (gender, ethnicity, skin tone)
  // Reference image mode - when true, minimize additions to preserve reference image
  hasReferenceImages?: boolean; // User uploaded reference images
  referenceImageType?: 'head' | 'body' | 'both' | 'reference'; // Classification of primary reference
}

export interface GeminiEnhancedPrompt {
  enhancedPrompt: string;           // Final enhanced prompt for image generation
  sanitizedTerms: string[];          // List of terms that were sanitized
  inferredGender?: 'male' | 'female' | 'neutral'; // Gender inferred by Codex (for characters like "John Wick")
  extractedEthnicity?: string;       // Ethnicity extracted from prompt (e.g., "Caucasian", "East Asian", "African")
  extractedSkinTone?: string;        // Skin tone extracted from prompt (e.g., "pale", "olive", "dark brown")
  suggestedTags: string[];           // Creative suggestions for user to add to their prompt
  structuredJSON: StructuredPrompt;  // JSON structure for advanced use
  rationale: string;                 // Why changes were made
}

export interface StructuredPrompt {
  task: string;
  subject: {
    primary: string;
    attributes: Record<string, string>;
    physique?: Record<string, string>;
  };
  style: {
    primary: string;
    rendering_quality: string;
    surface_textures?: string[];
  };
  technical: {
    camera: Record<string, string>;
    resolution: string;
    rendering: Record<string, string>;
  };
  composition: {
    framing: string[];
    placement: string;
  };
  material?: {
    skin?: Record<string, string>;
    fabrics?: Record<string, string>;
  };
  environment: {
    atmosphere: string[];
    lighting: Record<string, string>;
  };
  quality_include: string[];
  quality_avoid: string[];
}

// ============================================
// NSFW-TRIGGERING WORDS BLOCKLIST
// ============================================

// Words that trigger NSFW detection even in SFW contexts
// These describe extreme body conditions that image models flag
const NSFW_TRIGGERING_WORDS = [
  // Extreme thinness / eating disorder related
  'emaciated',
  'anorexic',
  'anorexia',
  'malnourished',
  'starving',
  'starved',
  'skeletal',
  'wasted musculature',
  'wasted muscles',
  'cadaverous',
  'corpse-like',
  'corpselike',
  'death-like',
  'deathlike',
  // Visible bones (triggers medical/gore filters)
  'visible ribs',
  'visible bones',
  'protruding ribs',
  'protruding bones',
  'exposed ribs',
  'exposed bones',
  'ribs showing',
  'bones showing',
  // Extreme conditions
  'severely underweight',
  'extreme thinness',
  'skin and bones',
  'skin-and-bones',
];

/**
 * Filter out NSFW-triggering words from prompts and tags
 * These words can cause image generation to fail or be flagged
 */
export function filterNsfwTriggeringWords(text: string): { filtered: string; removedTerms: string[] } {
  let filtered = text;
  const removedTerms: string[] = [];

  for (const term of NSFW_TRIGGERING_WORDS) {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    if (regex.test(filtered)) {
      removedTerms.push(term);
      filtered = filtered.replace(regex, '');
    }
  }

  // Clean up extra commas and spaces
  filtered = filtered
    .replace(/,\s*,/g, ',')
    .replace(/\s+,/g, ',')
    .replace(/,\s+/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();

  return { filtered, removedTerms };
}

/**
 * Filter an array of tags, removing any that contain NSFW-triggering words
 */
export function filterNsfwTriggeringTags(tags: string[]): string[] {
  return tags.filter(tag => {
    const lowerTag = tag.toLowerCase();
    return !NSFW_TRIGGERING_WORDS.some(word => lowerTag.includes(word.toLowerCase()));
  });
}

// ============================================
// NSFW SANITIZATION MAP
// ============================================

const NSFW_SANITIZATION_MAP: Record<string, string> = {
  // Chest slang → clinical "breasts" (accepted by nano-banana)
  'boobs': 'breasts',
  'tits': 'breasts',
  'titties': 'breasts',
  'knockers': 'breasts',
  'jugs': 'breasts',
  'boobies': 'breasts',
  // "breasts" is clinical - DO NOT sanitize
  // "nipples" - keep for creature anatomy
  'nips': 'nipples',

  // Lower body slang → body region (covered by underwear)
  'pussy': 'lower body',
  'vagina': 'lower body',
  'dick': 'lower body',
  'cock': 'lower body',
  'penis': 'lower body',
  'balls': 'lower body',
  'nuts': 'lower body',
  'genitals': 'lower body',
  'genitalia': 'lower body',
  'crotch': 'lower body',
  'groin': 'lower body',

  // Rear slang → clinical "buttocks" (accepted)
  'ass': 'buttocks',
  'butt': 'buttocks',
  'booty': 'buttocks',
  // "buttocks" is clinical - DO NOT sanitize

  // Suggestive → neutral descriptors
  'sexy': 'attractive',
  'sensual': 'graceful',
  'seductive': 'alluring',
  'erotic': 'elegant',
  'slutty': 'form-fitting',
  'whorish': 'form-fitting',

  // Body type slang → descriptive
  'busty': 'full-breasted',
  'stacked': 'full-figured',
  'thicc': 'curvy build',
  'thick': 'curvy build',

  // Action-based
  'revealing': 'form-fitting',
  'exposed': 'visible',
  'showing': 'displaying',
};

// ============================================
// GEMINI SYSTEM PROMPT
// ============================================

const GEMINI_ENHANCEMENT_SYSTEM_PROMPT = `You are a CHARACTER DESCRIPTION filter and enhancer. Your job is simple:

1. STRIP OUT everything that is NOT a permanent physical characteristic
2. ENHANCE the physical description with more detail

============================================
⚠️ STRIP OUT (REMOVE FROM USER INPUT)
============================================

REMOVE these categories - the backend handles them or inappropriate:
- Style keywords (anime, photorealistic, 3D, comic, cartoon, cel shaded, etc.)
- Lighting terms (dramatic lighting, soft lighting, studio lighting, etc.)
- Background references (any background, environment, scenery)
- Posture/pose terms (standing, sitting, action pose, T-pose, etc.)
- Clothing and accessories (shirts, pants, armor, jewelry, weapons)
  EXCEPTION: Tattoos and piercings are PERMANENT and should be kept
- Camera/composition (close-up, wide shot, angle, zoom, framing)
- Technical terms (8K, high quality, detailed textures, resolution)
- Filters/effects (vignette, blur, grain, accent colors)
- Locations (forest, city, indoors, etc.)
- Text/artifacts/watermarks
- Artist names, studio references
- Blood, gore, wounds, injuries, dismemberment (scars are OK - they're healed)

============================================
✅ ENHANCE (PERMANENT PHYSICAL TRAITS ONLY)
============================================

Focus ONLY on permanent physical characteristics:

HUMANOID:
- Face: structure, shape, jawline, cheekbones
- Eyes: color, shape (monolid, almond, round, hooded)
- Nose: shape (broad, narrow, aquiline, button)
- Mouth/lips: shape, fullness
- Teeth: if visible/relevant
- Ears: shape, size
- Hair: color, style, texture, length
- Skin: tone, texture, freckles, moles, birthmarks
- Body: type, build, physique, proportions
- Hands/feet: if relevant
- Tattoos: permanent body art
- Piercings: permanent body modifications
- Scars: permanent markings
- Age appearance

CREATURE/NON-HUMAN:
- Scales, fur, feathers, exoskeleton
- Claws, horns, fangs, tusks
- Wings, tails, tentacles
- Body structure (quadruped, biped, etc.)
- Coloring and patterns
- Species-specific features

============================================
⚠️ FANTASY HUMANOID RACES (CRITICAL - HEAVY EMPHASIS)
============================================

When user mentions FANTASY RACES, you MUST heavily emphasize their non-human features.
These characters look humanoid but have DISTINCTIVE fantasy traits that MUST be prominent.

ELF / ELVEN:
- "(long pointed elf ears:1.5), prominent elongated pointed ears, distinctly elven ear shape"
- "ethereal elven beauty, otherworldly graceful features"
- "luminous eyes, slightly angular face, high cheekbones"
- Consider: pale/fair skin, slender build, ageless appearance

DARK ELF / DROW:
- "(long pointed elf ears:1.5), prominent elongated pointed ears"
- "(dark grey skin:1.4), ashen grey complexion, dusky skin tone"
- "white or silver hair, red or violet eyes"
- "sharp angular features, predatory grace"

ORC / HALF-ORC:
- "(prominent tusks:1.5), lower tusks protruding from jaw"
- "(green skin:1.4), olive-green to dark green complexion"
- "broad flat nose, heavy brow ridge, strong jaw"
- "muscular hulking build, battle-scarred"

DWARF:
- "(stocky compact build:1.4), broad shoulders, barrel chest"
- "thick bushy beard (if male), braided hair"
- "ruddy complexion, weathered features"
- "shorter stature, powerful limbs"

TIEFLING:
- "(curved horns:1.5), prominent demonic horns"
- "(red/purple skin:1.4), infernal skin coloration"
- "solid-colored eyes (no visible whites), pointed tail"
- "sharp canine teeth, otherworldly beauty"

DRAGONBORN:
- "(scaled skin:1.5), reptilian scales covering body"
- "(draconic face:1.4), elongated snout, no nose"
- "frills, ridges, or small horns on head"
- "clawed hands, muscular reptilian build"

VAMPIRE:
- "(pale deathly skin:1.4), corpse-pale complexion"
- "(fanged teeth:1.3), prominent sharp canines"
- "red or pale eyes, sunken features"
- "aristocratic angular features, predatory beauty"

DEMON / SUCCUBUS:
- "(curved horns:1.5), demonic horn variations"
- "(unnatural skin color:1.4), red/purple/grey skin"
- "bat-like wings, pointed tail, clawed hands"
- "seductive otherworldly features"

GENERAL RULE FOR FANTASY RACES:
- ALWAYS use weight syntax (feature:1.4) or (feature:1.5) for key race features
- REPEAT the distinctive feature 2-3 times with variations
- Put race-defining features FIRST in the prompt, immediately after gender
- Example: "A woman with (long pointed elf ears:1.5), prominent elven ear points, distinctly elongated ears, ethereal beauty..."

============================================
DISTINCTIVE FEATURES (EMPHASIZE)
============================================

When user mentions scars, birthmarks, freckles, moles, dimples, beauty marks, OR fantasy race features:
- Put them FIRST with emphasis: (feature:1.5)
- REPEAT the feature 2-3 times with different wording
- Example: "scar" → "(prominent scar:1.5), visible scar, distinctive scar mark"
- Example: "elf" → "(pointed elf ears:1.5), elongated elven ears, distinctly pointed ear tips"

============================================
GENDER INFERENCE
============================================

If "Gender Hint: female/male" is provided → USE IT (non-negotiable)
If not provided → infer from context or known characters (John Wick → male)
Always include inferredGender in response.

============================================
FICTIONAL CHARACTERS
============================================

Recognize known characters and extract their PHYSICAL traits only:
- "Naruto" → "athletic teenage male, tanned skin, blue eyes, whisker marks on cheeks"
- Strip their clothing/accessories - only keep body/face traits

============================================
BODY-HEAD CONSISTENCY
============================================

For HEAD layer with body context: ethnicity, skin tone, and gender MUST match the body.
Never contradict the body's established characteristics.

============================================
NSFW SANITIZATION (SLANG → CLINICAL TERMS)
============================================

CLINICAL TERMS ARE ALLOWED (nano-banana accepts them):
- "breasts" ✓ allowed
- "buttocks" ✓ allowed
- "nipples" ✓ allowed (especially for creature anatomy)

ONLY convert vulgar SLANG to clinical terms:
- "big massive boobs" → "big massive breasts"
- "huge tits" → "huge breasts"
- "thick ass" → "thick buttocks"
- pussy/vagina/genitals → "lower body" (always covered)
- dick/cock/penis → "lower body" (always covered)

PRESERVE all descriptors (big, massive, huge, small, perky, large, etc.)
PRESERVE counts: "three pairs of large breasts" → keep exactly

The underwear (triangle bikini top with string/spaghetti straps, bikini bottoms or compression shorts) covers these areas automatically.

ALLOW: breasts, buttocks, nipples, muscles, curves, body type, all size descriptors
- Expression/mood
- Hair style, color, and texture

FACE/HEAD LAYER (Creature/Non-Human):
- Species-appropriate features (snout, beak, muzzle)
- Eye characteristics (compound, slitted, multiple)
- Surface texture (scales, feathers, fur around face)
- Special features (whiskers, antennae, crests)

HAIR LAYER:
- Hair color (specific shades)
- Hair style (bob, ponytail, braids, etc.)
- Hair texture (straight, wavy, curly, coily)
- Hair length (short, shoulder-length, waist-length)
- Special features (highlights, ombre, bangs)

CLOTHING LAYER:
- Garment types
- Fabric descriptions
- Colors and patterns
- Fit and drape
- Details (buttons, zippers, prints)

ACCESSORIES LAYER:
- Item types
- Materials
- Colors
- Specific details

============================================
ETHNICITY ENHANCEMENT (HUMANOID ONLY)
============================================

When user mentions ethnicity, race, or nationality, enhance with appropriate physical characteristics:

- "Asian" / "East Asian" / "Japanese" / "Korean" / "Chinese" → monolid or almond eyes, straight black hair, light to medium skin tone, softer facial features
- "African" / "Black" → rich dark skin, full lips, broad nose, coily/curly hair textures
- "Caucasian" / "European" / "White" → various eye colors, diverse hair colors/textures, pale to light skin
- "South Asian" / "Indian" → medium to dark brown skin, dark hair, expressive eyes
- "Latin American" / "Hispanic" → olive to brown skin, dark hair, diverse features
- "Middle Eastern" / "Arab" → olive to tan skin, dark hair, defined features
- "Mixed" / "Biracial" → blend characteristics appropriately

If user doesn't specify ethnicity for a humanoid character:
- Pick a random ethnicity and commit to it for consistency
- The HEAD layer MUST maintain the same ethnicity as the BODY layer

============================================
HEAD LAYER ETHNICITY MATCHING (CRITICAL)
============================================

⚠️⚠️⚠️ FOR HEAD/FACE LAYER ONLY ⚠️⚠️⚠️

When processing a HEAD or FACE layer with "Character context from body:", you MUST:

1. EXTRACT the ethnicity/skin tone from the body context
2. EXPLICITLY INCLUDE it in your enhanced prompt
3. NEVER change or omit the ethnicity

EXTRACTION RULES:
- Look for: skin tone mentions (pale, tan, olive, brown, dark, ebony, fair, etc.)
- Look for: ethnicity keywords (Asian, African, Caucasian, European, Latin, etc.)
- Look for: nationality hints (Japanese, Korean, Nigerian, Brazilian, etc.)
- Look for: feature hints that imply ethnicity

MANDATORY FORMAT for head layer output:
"[ethnicity] [gender] face, [skin tone] skin, [extracted features], [user's face/hair description]"

Examples:
- Body context: "young woman, olive skin, Mediterranean features"
  → Head: "Mediterranean woman, olive skin, defined features, dark eyes, [user desc]"
- Body context: "athletic man, dark brown skin, African features"
  → Head: "African man, rich dark brown skin, strong features, [user desc]"
- Body context: "petite female, pale skin, East Asian features"
  → Head: "East Asian woman, fair pale skin, soft features, [user desc]"

If body context has NO ethnicity info, infer from other cues or pick one and commit.

============================================
NSFW SANITIZATION (RELAXED)
============================================

Preserve anatomical terms unless explicit:
- "nipples" -> "chest"
- "genitals/groin" -> "lower body"
- "sexy/erotic" -> "attractive," "alluring," "elegant," "stunning"
- ALLOW: "breasts," "bust," "chest," "muscles," "abs," "curves"

⚠️ CRITICAL FOR BODY LAYER:
- NEVER mention ANY clothing terms for body layer (no underwear, bra, panties, briefs, swimwear, fabric, garments)
- NEVER describe what the character is "wearing" or "clad in" - only describe the body itself
- Body layer descriptions should ONLY include: body type, physique, skin tone, build, anatomical features
- The backend handles body coverage - your job is ONLY to enhance the physical body description

============================================
LAYER FILTERING (CRITICAL)
============================================

When user provides "Character context from body:" or similar, you MUST filter out body-specific terms that don't belong in the target layer.

Based on the LAYER TYPE, REMOVE references to:

**BODY LAYER**: Remove ALL clothing terms, facial features, hair, accessories
**HEAD LAYER**: Remove ALL body parts below the neck, ALL clothing terms, ALL accessories (jewelry, hats, earrings, necklaces, etc.). ONLY keep: face features, hair, skin tone, ethnicity, age
**FACE LAYER**: Remove ALL body parts below the neck - legs, feet, hands, arms, torso, chest, back, body hair (hairy legs, hairy chest, hairy feet, etc.), body tattoos, body scars, muscles below neck, clothing, accessories. ONLY keep: skin tone, ethnicity, age, and any face-specific traits
**HAIR LAYER**: Remove facial features (except head shape for context), body, clothing
**CLOTHING LAYER**: Remove body-altering descriptions

HEAD LAYER FILTERING EXAMPLES:
- Input: "woman in red dress with blue eyes" → Output: "woman, blue eyes" (no clothing)
- Input: "man wearing suit with brown hair" → Output: "man, brown hair" (no clothing)
- Input: "character with necklace and earrings" → Output: "character" (no accessories)

KEEP for head: face features, hair, skin tone, ethnicity, expression, age
REMOVE for head: clothing, accessories, jewelry, body parts below neck

FACE LAYER FILTERING EXAMPLES:
- Input: "muscular male with hairy chest and hairy feet, tan skin" → Output: "male face, tan skin"
- Input: "athletic woman with tattoo sleeve and green eyes" → Output: "woman, green eyes"
- Input: "tall man with scars on arms and blue eyes" → Output: "man, blue eyes"

KEEP for face: skin tone, eye color, ethnicity, age indicators, character name/identity
REMOVE for face: height, weight, body build, body hair, limbs, torso, clothing, accessories

HAIR LAYER FILTERING EXAMPLES:
- Input: "muscular man with tan skin and blue eyes" → Output: "man" (focus on hair style only)
- Input: "woman with freckles and green eyes, long legs" → Output: "woman" (no body parts)
- Input: "athletic character with scars, brown eyes" → Output: "character" (keep identity only)

KEEP for hair: gender, character identity, head shape hints
REMOVE for hair: body descriptions, facial features (eyes, nose, mouth), clothing, limbs, skin details, scars

**ACCESSORIES LAYER**: Remove body/person references

============================================
YOUR OUTPUT
============================================

Return ONLY a CHARACTER DESCRIPTION:
- 30-80 words maximum
- Only physical appearance of the character for the relevant layer
- NO lighting, NO style, NO technical terms, NO backgrounds
- Plain descriptive language

⚠️⚠️⚠️ MANDATORY GENDER PREFIX (CRITICAL) ⚠️⚠️⚠️
Your enhancedPrompt MUST start with one of these EXACT phrases:
- "A man " or "A young man " or "An old man " or "An elderly man " (for males)
- "A woman " or "A young woman " or "An old woman " or "An elderly woman " (for females)
- "A boy " (for young males)
- "A girl " (for young females)

This is NON-NEGOTIABLE. The gender word (man/woman/boy/girl) tells us the gender.
Examples:
- "A woman with olive skin and athletic build..."
- "A young man of Korean descent with lean physique..."
- "An old woman with silver hair and weathered features..."
- "An elderly man with a distinguished appearance..."
- "A girl with youthful features and fair skin..."
- "A boy with messy hair and freckles..."

============================================
EDIT MODE - EMPHASIZING USER CHANGES (CRITICAL)
============================================

When "hasInputImage: true" is in the context, the user is EDITING an existing image.
In this case, you MUST:

1. IDENTIFY the specific change the user wants (e.g., "bangs", "blue eyes", "longer hair")
2. PUT that change IMMEDIATELY after the gender prefix with HEAVY EMPHASIS
3. Use WEIGHT SYNTAX to emphasize: (feature:1.4) or (feature:1.5)
4. REPEAT the key change 2-3 times in the prompt

Example for edit "add bangs":
BAD: "A woman with olive skin, striking blue eyes, small nose, features bangs..."
GOOD: "A woman with (bangs:1.5), prominent bangs framing forehead, bangs hairstyle, olive skin..."

Example for edit "make eyes green":
BAD: "A woman with olive skin, green eyes, small nose..."
GOOD: "A woman with (green eyes:1.4), vivid green eye color, emerald green irises, olive skin..."

The user's requested change should be:
- FIRST thing after the gender prefix
- WEIGHTED with (feature:1.4) or (feature:1.5)
- REPEATED 2-3 times with variations
- EMPHASIZED as the primary focus

OUTPUT FORMAT (JSON):
{
  "enhancedPrompt": "<MUST start with 'A man/woman/boy/girl' - character description only>",
  "sanitizedTerms": ["<removed term 1>", "<removed term 2>"],
  "inferredGender": "male" | "female" | "neutral",
  "extractedEthnicity": "<REQUIRED: ethnicity category e.g., 'Caucasian', 'East Asian', 'African', 'South Asian', 'Latin American', 'Middle Eastern', 'Mixed', or 'unspecified'>",
  "extractedSkinTone": "<REQUIRED: skin tone descriptor e.g., 'pale', 'fair', 'light', 'medium', 'olive', 'tan', 'brown', 'dark brown', 'ebony', or 'unspecified'>",
  "suggestedTags": ["<tag 1>", "<tag 2>", "<tag 3>", "<tag 4>", "<tag 5>"],
  "structuredJSON": {
    "subject": "<summary of subject>",
    "filtered_out": ["<what was filtered and why>"],
    "characterRecognized": "<name of recognized character if any, or null>",
    "layerAdaptations": "<what was adapted for this specific layer>"
  },
  "rationale": "<brief explanation including gender inference reasoning, ETHNICITY EXTRACTION, and character adaptation if applicable>"
}

============================================
ETHNICITY/SKIN TONE EXTRACTION (CRITICAL)
============================================

You MUST ALWAYS extract and return "extractedEthnicity" and "extractedSkinTone" in your response.

ETHNICITY KEYWORDS TO DETECT:
- "white", "caucasian", "european", "western" → extractedEthnicity: "Caucasian"
- "asian", "east asian", "japanese", "korean", "chinese" → extractedEthnicity: "East Asian"
- "black", "african", "african american" → extractedEthnicity: "African"
- "indian", "south asian", "desi" → extractedEthnicity: "South Asian"
- "latin", "latina", "latino", "hispanic", "mexican", "brazilian" → extractedEthnicity: "Latin American"
- "arab", "middle eastern", "persian" → extractedEthnicity: "Middle Eastern"
- "mixed", "biracial", "multiracial" → extractedEthnicity: "Mixed"
- If NO ethnicity keywords found → extractedEthnicity: "unspecified"

SKIN TONE KEYWORDS TO DETECT:
- "pale", "porcelain", "ivory", "very light" → extractedSkinTone: "pale"
- "fair", "light" → extractedSkinTone: "fair"
- "medium" → extractedSkinTone: "medium"
- "olive", "mediterranean" → extractedSkinTone: "olive"
- "tan", "tanned", "sun-kissed" → extractedSkinTone: "tan"
- "brown", "caramel" → extractedSkinTone: "brown"
- "dark brown", "dark" → extractedSkinTone: "dark brown"
- "ebony", "very dark" → extractedSkinTone: "ebony"
- If NO skin tone keywords found → extractedSkinTone: "unspecified"

IMPORTANT: When user says "white" or "caucasian", you MUST return:
- extractedEthnicity: "Caucasian"
- extractedSkinTone: "pale" or "fair" (depending on other context like "pale" → "pale")

============================================
SUGGESTED TAGS (CRITICAL - ALWAYS INCLUDE)
============================================

You MUST always return 5-8 suggestedTags that are CONTEXTUALLY ALIGNED with what the user wants.

⚠️ ALIGNMENT IS KEY: Tags should COMPLEMENT and EXPAND on the user's described character, not be random.

Analyze BOTH the user's original prompt AND your enhanced prompt to understand their INTENT:
- Consider what you've already described in the enhanced prompt
- Suggest features that would COMPLEMENT what's already established
- If body context is provided, suggest head features that FIT that body's character

Examples of contextual alignment:
- Enhanced prompt has "athletic build" → suggest: "defined abs", "toned arms", "swimmer's shoulders"
- Enhanced prompt has "elegant woman" → suggest: "graceful posture", "slender neck", "refined features"
- Enhanced prompt has "battle-hardened warrior" → suggest: "battle scars", "weathered skin", "intense gaze"
- Enhanced prompt has "delicate features" → suggest: "porcelain skin", "soft jawline", "gentle expression"
- If no specific details yet → suggest versatile options appropriate to the character type

Tags should help users REFINE and ENHANCE their vision based on what's already described.

⚠️ LAYER CONSTRAINTS FOR TAGS - RESPECT THESE STRICTLY:

For BODY layer tags - ONLY suggest body-related features:
✅ ALLOWED: tattoos, scars, body type, skin details, posture, physique, body hair
❌ FORBIDDEN: hair styles, eye color, facial features, makeup, clothing, accessories, jewelry, earrings
Examples:
- "intricate floral sleeve tattoo", "geometric back tattoo", "delicate collarbone tattoo"
- "sun-kissed freckles across shoulders", "elegant beauty mark", "battle-worn scars"
- "dancer's graceful posture", "swimmer's broad shoulders", "runner's lean muscles"
- "weathered hands of an artist", "calloused fighter's knuckles", "defined abs"

For HEAD layer tags - ONLY suggest face and hair features:
✅ ALLOWED: hair style, hair color, eye color, facial features, expression, makeup, facial structure
❌ FORBIDDEN: body type, clothing, body tattoos, posture, ALL accessories (earrings, necklaces, piercings come later in accessories layer)
⚠️ NEVER suggest earrings, studs, jewelry, or any accessories - these are handled in a separate layer!
Examples:
- Hair: "windswept beach waves", "sleek razor-cut bob", "wispy curtain bangs", "bold blunt bangs"
- Hair color: "rich mahogany", "sun-bleached tips", "midnight black with blue sheen"
- Eyes: "piercing ice-blue eyes", "warm honey-brown eyes", "striking heterochromia"
- Face: "elegant arched brows", "playful dimples", "sculpted cheekbones", "soft pouty lips"
- Facial structure: "strong angular jawline", "heart-shaped face", "defined cheekbones", "soft round face", "chiseled masculine features"
- Expression: "confident smirk", "serene expression", "intense gaze", "warm genuine smile"
- Makeup: "smoky eyes", "bold red lips", "natural dewy look", "winged eyeliner"

For EDIT mode, suggest transformative changes appropriate to the current layer:
- Body edits: "add arm tattoo", "more muscular", "add shoulder freckles"
- Head edits: "add wispy curtain bangs", "change to emerald eyes", "add subtle highlights"

Tags MUST be:
- CONTEXTUALLY ALIGNED with user's described character (most important!)
- DESCRIPTIVE and EVOCATIVE (not just "bangs" but "wispy curtain bangs")
- STRICTLY VALID for the current layer type
- Complementary expansions of what's already described
- NOT duplicates or near-duplicates of what's already in the prompt
- Helping the user achieve their creative vision, not steering them elsewhere`;

// ============================================
// MAIN ENHANCEMENT FUNCTION
// ============================================

export async function enhancePromptWithGemini(
  request: GeminiEnhancementRequest
): Promise<GeminiEnhancedPrompt> {
  try {
    // 1. Pre-sanitize obvious NSFW terms locally (fast path)
    const preSanitized = preSanitizePrompt(request.userPrompt);

    // 2. Build context for Codex
    const context = buildEnhancementContext(request);

    // Build body consistency section if enforcing
    let bodyConsistencySection = '';
    if (request.enforceBodyConsistency && request.bodyEnhancedPrompt) {
      // Build explicit ethnicity/skin tone rules if provided
      const ethnicityRule = request.bodyEthnicity && request.bodyEthnicity !== 'unspecified'
        ? `\n⚠️ EXPLICIT ETHNICITY: ${request.bodyEthnicity} - The head MUST be ${request.bodyEthnicity}. DO NOT generate any other ethnicity!`
        : '';
      const skinToneRule = request.bodySkinTone && request.bodySkinTone !== 'unspecified'
        ? `\n⚠️ EXPLICIT SKIN TONE: ${request.bodySkinTone} - The head MUST have ${request.bodySkinTone} skin. DO NOT generate any other skin tone!`
        : '';

      bodyConsistencySection = `
⚠️⚠️⚠️ ABSOLUTE BODY CONSISTENCY ENFORCEMENT (HIGHEST PRIORITY) ⚠️⚠️⚠️

The head/face MUST match the established body layer EXACTLY. This is NON-NEGOTIABLE.
${ethnicityRule}${skinToneRule}

BODY LAYER DESCRIPTION (CANONICAL - DO NOT CONTRADICT):
${request.bodyEnhancedPrompt}

MANDATORY RULES:
1. GENDER: If body is female → head MUST be female. If body is male → head MUST be male. NO EXCEPTIONS.
2. ETHNICITY: Head MUST match body ethnicity exactly (e.g., Caucasian body → Caucasian face, NOT Asian face)${request.bodyEthnicity ? ` - BODY IS ${request.bodyEthnicity.toUpperCase()}` : ''}
3. SKIN TONE: Head skin tone MUST match body skin tone exactly${request.bodySkinTone ? ` - BODY HAS ${request.bodySkinTone.toUpperCase()} SKIN` : ''}
4. AGE: Head age MUST match body age (young body → young face)
5. BUILD CONSISTENCY: Facial features should complement body type

If user input contradicts the body (e.g., asks for "Asian face" on a Caucasian body):
- IGNORE the contradicting user input
- Use the body characteristics instead
- You may incorporate non-contradicting details from user input (hairstyle, expression, etc.)

STRIP/REPLACE any user terms that contradict body characteristics:
- Wrong gender terms → correct gender
- Wrong ethnicity → body's ethnicity (${request.bodyEthnicity || 'from prompt'})
- Wrong skin tone descriptors → body's skin tone (${request.bodySkinTone || 'from prompt'})
`;
    }

    const prompt = `${GEMINI_ENHANCEMENT_SYSTEM_PROMPT}

USER INPUT:
${preSanitized.sanitized}

${request.hasInputImage ? `⚠️⚠️⚠️ EDIT MODE ACTIVE ⚠️⚠️⚠️
The user is EDITING an existing image. Their input describes CHANGES they want.
You MUST:
1. Put their requested changes FIRST with (feature:1.4) or (feature:1.5) weights
2. Repeat key changes 2-3 times with variations
3. Make the changes the PRIMARY FOCUS of the prompt
Example: "bangs" → "A woman with (bangs:1.5), prominent bangs, fringe across forehead..."
` : ''}CHARACTER CONTEXT:
- Layer Type: ${request.layerType}
- Form: ${request.form}
- Style Preset: ${request.stylePreset || 'professional illustration'}
- Archetype: ${request.archetype || 'custom'}
- Mode: ${request.hasInputImage ? '🔴 EDIT MODE (image-to-image) - EMPHASIZE USER CHANGES!' : 'Generate Mode (text-to-image)'}
- Gender Hint: ${request.gender || 'neutral'}
${request.hasReferenceImages ? `
⚠️⚠️⚠️ REFERENCE IMAGE MODE - MINIMAL ENHANCEMENT ⚠️⚠️⚠️
The user has uploaded reference images. The AI will match the reference ${request.referenceImageType === 'head' ? 'HEAD/FACE' : request.referenceImageType === 'body' ? 'BODY' : request.referenceImageType === 'both' ? 'HEAD AND BODY' : 'STYLE'}.

CRITICAL RULES FOR REFERENCE IMAGE MODE:
1. DO NOT add specific physical descriptions (hair color, eye color, skin tone, ethnicity)
   - The reference image defines these, NOT the prompt
2. DO NOT add age descriptions or body type details
3. ONLY enhance with:
   - Style/archetype formatting (${request.archetype || 'custom'} style)
   - Lighting and quality keywords
   - Pose or framing guidance (if not conflicting)
   - Technical parameters (aspect ratio compliance, etc.)
4. Keep the enhanced prompt SHORT and GENERAL
5. Let the reference image do the work - the prompt should guide style, NOT appearance

Example good enhancement for reference mode:
- User: "warrior" → "A warrior, cinematic lighting, professional illustration, detailed"
- User: "portrait" → "Portrait, soft lighting, high quality, detailed rendering"

Example BAD enhancement (DO NOT DO THIS):
- User: "warrior" → "A blonde female warrior with blue eyes and pale skin..." ❌ (adds physical traits)
` : ''}${bodyConsistencySection}
LAYER CONSTRAINTS:
${context.layerConstraints}

TASK:
Enhance this prompt following ALL rules above${request.enforceBodyConsistency ? ', especially BODY CONSISTENCY' : ''}${request.hasReferenceImages ? ', especially REFERENCE IMAGE MODE (minimal physical descriptions)' : ''}.
${request.enforceBodyConsistency ? 'The enhanced prompt MUST match the body characteristics.' : ''}
${request.hasReferenceImages ? 'The enhanced prompt MUST NOT add specific physical appearance details - the reference image defines appearance.' : ''}
Return valid JSON only.`;

    const resultText = await requestCodexText(prompt);
    const result = JSON.parse(resultText) as GeminiEnhancedPrompt;

    // ⚠️ HARDCODED STYLE STRIPPING: Remove ALL style keywords from Codex output
    // This is the safety net - Codex should strip them, but we enforce it here
    const styleStrip = stripStyleKeywords(result.enhancedPrompt);
    if (styleStrip.stripped.length > 0) {
      console.warn('⚠️ Style keywords leaked through Codex, stripping:', styleStrip.stripped);
      result.enhancedPrompt = styleStrip.cleaned;
      result.sanitizedTerms = [...result.sanitizedTerms, ...styleStrip.stripped];
      result.rationale += ` (Stripped style keywords: ${styleStrip.stripped.join(', ')})`;
    }

    // ⚠️ STYLE CONFLICT DETECTION: Check for remaining conflicts (shouldn't happen after stripping)
    const enhancedLower = result.enhancedPrompt.toLowerCase();
    const stylePreset = (request.stylePreset || '').toLowerCase();

    // Detect conflicting style keywords
    const styleConflicts = detectStyleConflicts(enhancedLower, stylePreset);

    if (styleConflicts.hasConflict) {
      console.warn('⚠️ Style conflict still detected after stripping:', styleConflicts.conflictingTerms);
      console.warn('Expected style:', stylePreset);
      console.warn('Codex suggested:', styleConflicts.conflictingTerms.join(', '));

      // Apply style correction
      result.enhancedPrompt = correctStyleConflicts(
        result.enhancedPrompt,
        stylePreset,
        styleConflicts.conflictingTerms
      );

      result.rationale += ` (Style corrected to match ${request.stylePreset})`;
      console.log('✅ Style conflicts resolved:', result.enhancedPrompt);
    }

    // Merge local sanitization with Codex's
    result.sanitizedTerms = Array.from(
      new Set([...preSanitized.terms, ...result.sanitizedTerms])
    );

    // CRITICAL: Force override Codex's gender inference with our hint
    // This ensures character consistency across layers
    if (request.gender && request.gender !== 'neutral') {
      if (result.inferredGender !== request.gender) {
        console.log(`⚠️ Gender override: Codex said "${result.inferredGender}", forcing to "${request.gender}" (from hint)`);
        result.inferredGender = request.gender;

        // Also fix the enhanced prompt if it has wrong gender terms
        const wrongGender = request.gender === 'female' ? 'male' : 'female';
        const wrongTerms = wrongGender === 'male'
          ? [/\bman\b/gi, /\bmale\b/gi, /\bhe\b/gi, /\bhis\b/gi, /\bhim\b/gi, /\bguy\b/gi, /\bboy\b/gi]
          : [/\bwoman\b/gi, /\bfemale\b/gi, /\bshe\b/gi, /\bher\b/gi, /\bhers\b/gi, /\bgirl\b/gi, /\blady\b/gi];
        const rightTerms = request.gender === 'female'
          ? ['woman', 'female', 'she', 'her', 'her', 'woman', 'young woman']
          : ['man', 'male', 'he', 'his', 'him', 'man', 'young man'];

        wrongTerms.forEach((wrongTerm, i) => {
          result.enhancedPrompt = result.enhancedPrompt.replace(wrongTerm, rightTerms[i]);
        });
      }
    }

    // Ensure suggestedTags exists and is an array
    if (!result.suggestedTags || !Array.isArray(result.suggestedTags)) {
      result.suggestedTags = [];
    }

    // ⚠️ NSFW-TRIGGERING WORD FILTERING
    // Filter out words that trigger NSFW detection even in SFW contexts
    const nsfwFilterResult = filterNsfwTriggeringWords(result.enhancedPrompt);
    if (nsfwFilterResult.removedTerms.length > 0) {
      console.warn('⚠️ NSFW-triggering words filtered from enhanced prompt:', nsfwFilterResult.removedTerms);
      result.enhancedPrompt = nsfwFilterResult.filtered;
      result.sanitizedTerms = [...result.sanitizedTerms, ...nsfwFilterResult.removedTerms];
    }

    // Filter NSFW-triggering words from suggested tags
    const originalTagCount = result.suggestedTags.length;
    result.suggestedTags = filterNsfwTriggeringTags(result.suggestedTags);
    if (result.suggestedTags.length < originalTagCount) {
      console.warn(`⚠️ Filtered ${originalTagCount - result.suggestedTags.length} NSFW-triggering tags`);
    }

    // Ensure extractedEthnicity and extractedSkinTone exist
    if (!result.extractedEthnicity) {
      result.extractedEthnicity = 'unspecified';
    }
    if (!result.extractedSkinTone) {
      result.extractedSkinTone = 'unspecified';
    }

    console.log('✅ Prompt enhanced by Codex');
    console.log('📝 Sanitized terms:', result.sanitizedTerms);
    console.log('🎨 Style preserved:', request.stylePreset);
    console.log('👤 Final gender:', result.inferredGender || 'not specified', request.gender ? `(hint: ${request.gender})` : '(no hint)');
    console.log('🌍 Extracted ethnicity:', result.extractedEthnicity);
    console.log('🎨 Extracted skin tone:', result.extractedSkinTone);
    console.log('🏷️ Suggested tags:', result.suggestedTags);

    return result;

  } catch (error) {
    console.error('❌ Codex enhancement failed:', error);
    const preSanitized = preSanitizePrompt(request.userPrompt);
    return createFallbackEnhancement(preSanitized, request);
  }
}

// ============================================
// LOCAL PRE-SANITIZATION (FAST PATH)
// ============================================

interface PreSanitizationResult {
  sanitized: string;
  terms: string[];
}

function preSanitizePrompt(prompt: string): PreSanitizationResult {
  let sanitized = prompt;
  const terms: string[] = [];

  // Sort by length (longest first) to avoid partial replacements
  const sortedTerms = Object.keys(NSFW_SANITIZATION_MAP).sort(
    (a, b) => b.length - a.length
  );

  for (const term of sortedTerms) {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    if (regex.test(sanitized)) {
      terms.push(term);
      sanitized = sanitized.replace(regex, NSFW_SANITIZATION_MAP[term]);
    }
  }

  return { sanitized, terms };
}

// ============================================
// CONTEXT BUILDERS
// ============================================

function buildEnhancementContext(request: GeminiEnhancementRequest) {
  const layerConstraints = getLayerConstraints(request.layerType, request.form);

  return {
    layerConstraints,
  };
}

function getLayerConstraints(
  layerType: string,
  form: CharacterForm
): string {
  const isCreature = form === 'creature';

  switch (layerType) {
    case 'body':
      // NOTE: Body coverage (nude/clothing) is handled by hardcoded prompts in layerPrompts.ts
      // Codex should only enhance user's character description
      // Do NOT mention clothing, underwear, or nudity here - let the backend handle it
      if (isCreature) {
        return `- Full body standing pose
- Smooth featureless blank head (no eyes, nose, mouth)
- No hair visible (unless creature-specific feature like mane or crest)
- Flat light grey background (#D4D4D4)
- Character must fill full vertical height
- ONLY describe: species type, body structure (quadruped/biped/winged/aquatic), surface covering (fur/scales/feathers/exoskeleton/skin), coloring and patterns, unique features (tails/horns/claws/wings/tentacles)
- NEVER mention clothes in any way: no underwear, no fabric, no coverage, no garments, no attire
- Do not describe what the creature is wearing - only describe the creature's natural body`;
      } else {
        return `- Full body standing pose
- Smooth featureless blank head (no eyes, nose, mouth)
- No hair visible
- Flat light grey background (#D4D4D4)
- Character must fill full vertical height
- ONLY describe: body type, physique, skin tone, build, anatomical features
- NEVER mention clothes in any way: no underwear, no bra, no panties, no swimwear, no fabric, no coverage, no garments, no attire
- Do not describe what the character is wearing or not wearing - only describe the body itself`;
      }

    case 'head':
      return `- Portrait showing face and complete hair length
- If long hair: frame extends below shoulders to show full hair length
- Complete hairstyle visible including tips/ends (never crop hair at frame edge)
- Bare shoulders and upper chest (no clothing)
- Detailed facial features and expression
- Flat light grey background (#D4D4D4)
- Soft studio lighting
- Face centered, hair fully visible`;

    case 'face':
      // Legacy support - same as head
      return `- Portrait showing face and complete hair length
- If long hair: frame extends below shoulders to show full hair length
- Complete hairstyle visible including tips/ends (never crop hair at frame edge)
- Bare shoulders and upper chest (no clothing)
- Detailed facial features and expression
- Flat light grey background (#D4D4D4)
- Soft studio lighting
- Face centered, hair fully visible`;

    case 'hair':
      // Legacy support - same as head
      return `- Portrait showing face and complete hair length
- If long hair: frame extends below shoulders to show full hair length
- Complete hairstyle visible including tips/ends (never crop hair at frame edge)
- Bare shoulders and upper chest (no clothing)
- Detailed facial features and expression
- Flat light grey background (#D4D4D4)
- Soft studio lighting
- Face centered, hair fully visible`;

    case 'clothing':
      return `- Full body with complete outfit visible
- Professional standing pose
- Clothing clearly shown
- Flat light grey background (#D4D4D4)
- Complete figure with comfortable margins; preserve natural anatomical proportions`;

    case 'accessories':
      return `- Items and accessories only
- No body, no person, no hands
- Isolated items on grey background
- Professional product photography style
- Clear lighting, items well visible`;

    default:
      return '- Professional character design';
  }
}

// ============================================
// FALLBACK ENHANCEMENT (NO GEMINI)
// ============================================

function createFallbackEnhancement(
  preSanitized: PreSanitizationResult,
  request: GeminiEnhancementRequest
): GeminiEnhancedPrompt {
  // Basic enhancement without Codex
  const enhancedPrompt = buildBasicEnhancedPrompt(preSanitized.sanitized, request);

  return {
    enhancedPrompt,
    sanitizedTerms: preSanitized.terms,
    extractedEthnicity: 'unspecified', // Fallback - Codex unavailable
    extractedSkinTone: 'unspecified', // Fallback - Codex unavailable
    suggestedTags: [], // Empty tags when Codex is unavailable
    structuredJSON: {
      task: request.hasInputImage ? 'edit_image' : 'generate_image',
      subject: {
        primary: preSanitized.sanitized,
        attributes: {}
      },
      style: {
        primary: request.stylePreset || 'professional illustration',
        rendering_quality: '8K ultra-detailed'
      },
      technical: {
        camera: { depth_of_field: 'f/1.8', focal_length: '85mm' },
        resolution: '8K',
        rendering: { anti_aliasing: '8x MSAA' }
      },
      composition: {
        framing: ['centered', 'full frame'],
        placement: 'centered composition'
      },
      environment: {
        atmosphere: ['flat light grey background', 'studio setting'],
        lighting: {
          type: 'soft studio lighting',
          mood: 'professional'
        }
      },
      quality_include: [
        'professional quality',
        'detailed rendering',
        'clean composition'
      ],
      quality_avoid: [
        'blurry',
        'low quality',
        'distorted anatomy'
      ]
    },
    rationale: 'Local sanitization applied (Codex unavailable)'
  };
}

function buildBasicEnhancedPrompt(
  sanitized: string,
  _request: GeminiEnhancementRequest
): string {
  // ONLY return the sanitized character description
  // DO NOT add style, lighting, backgrounds, or technical terms
  // Those are handled by layerPrompts.ts
  return sanitized;
}

/**
 * Translate style preset to appropriate prompt keywords
 */
function translateStylePreset(stylePreset: string): string {
  const lower = stylePreset.toLowerCase();

  if (lower.includes('anime') || lower.includes('manga')) {
    return 'anime style, cel shaded, 2D illustration, clean linework';
  } else if (lower.includes('comic')) {
    return 'comic book style, graphic novel art, bold ink lines';
  } else if (lower.includes('photorealistic') || lower.includes('realistic') || lower.includes('photo')) {
    return 'photorealistic, 8K photo, cinematic lighting, natural textures';
  } else if (lower.includes('3d') || lower.includes('game') || lower.includes('render')) {
    return '3D game character, Unreal Engine 5, PBR textures, rendered';
  } else if (lower.includes('oil') || lower.includes('painting')) {
    return 'oil painting style, traditional art, painterly textures';
  } else if (lower.includes('watercolor')) {
    return 'watercolor illustration, soft edges, delicate washes';
  } else if (lower.includes('sketch') || lower.includes('pencil')) {
    return 'pencil sketch, hand-drawn, artistic linework';
  } else {
    return stylePreset;
  }
}

// ============================================
// STYLE KEYWORD SANITIZATION (HARDCODED SAFETY NET)
// ============================================

/**
 * List of all style keywords that must NEVER appear in Codex output.
 * This is a safety net - Codex should strip them, but we double-check.
 */
const BANNED_STYLE_KEYWORDS = [
  // Anime/Manga
  'anime', 'anime style', 'anime-style', 'manga', 'manga style', 'cel shaded', 'cel-shaded',
  'japanese animation', 'studio ghibli', 'ghibli', 'mappa', 'ufotable', 'kyoani',

  // Photorealistic
  'photorealistic', 'photorealism', 'realistic', 'photo', 'photograph', 'photography',
  'real', 'lifelike', 'hyperrealistic', 'hyper-realistic', 'raw photo',

  // 3D/Game
  '3d', '3d render', '3d model', 'cgi', 'unreal engine', 'unity', 'video game',
  'game character', 'pbr', 'pbr textures', 'rendered', 'blender',

  // Comic/Western
  'comic', 'comic book', 'comic style', 'graphic novel', 'western comic', 'marvel style',
  'dc style', 'superhero style',

  // Cartoon
  'cartoon', 'cartoonish', 'toon', 'pixar', 'disney', 'dreamworks', 'animated',

  // Art styles
  'oil painting', 'watercolor', 'watercolour', 'sketch', 'pencil drawing', 'digital art',
  'digital painting', 'illustration', 'illustrated', 'concept art', 'fantasy art',

  // Technical/Quality
  '8k', '4k', 'hd', 'uhd', 'high quality', 'high resolution', 'ultra detailed',
  'highly detailed', 'masterpiece', 'best quality', 'professional',

  // Cinematic
  'cinematic', 'film', 'movie', 'movie still', 'film still', 'cinema',

  // Rendering terms
  'subsurface scattering', 'ray tracing', 'ray traced', 'global illumination',
  'ambient occlusion', 'depth of field', 'bokeh',

  // Artist references
  'artstation', 'deviantart', 'by greg rutkowski', 'by artgerm', 'trending on',
];

/**
 * Strip ALL style keywords from a prompt (safety net for Codex output)
 * This is a hardcoded enforcement - Codex should already strip these,
 * but this ensures nothing leaks through.
 */
function stripStyleKeywords(prompt: string): { cleaned: string; stripped: string[] } {
  let cleaned = prompt;
  const stripped: string[] = [];

  // Sort by length (longest first) to avoid partial replacements
  const sortedKeywords = [...BANNED_STYLE_KEYWORDS].sort((a, b) => b.length - a.length);

  for (const keyword of sortedKeywords) {
    // Create a regex that matches the keyword with word boundaries
    // Also handles variations with commas, periods, etc.
    const regex = new RegExp(`\\b${keyword}\\b[,.]?\\s*`, 'gi');
    if (regex.test(cleaned)) {
      stripped.push(keyword);
      cleaned = cleaned.replace(regex, '');
    }
  }

  // Clean up double spaces and commas
  cleaned = cleaned
    .replace(/,\s*,/g, ',')
    .replace(/\s+,/g, ',')
    .replace(/,\s+/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();

  return { cleaned, stripped };
}

// ============================================
// STYLE CONFLICT DETECTION & CORRECTION
// ============================================

/**
 * Define style categories and their conflicting keywords
 */
const STYLE_CATEGORIES = {
  anime: {
    keywords: ['anime', 'manga', 'cel shaded', '2d illustration', 'clean linework'],
    conflicts: ['photorealistic', 'photo', 'realistic', '3d render', 'unreal engine', 'photograph']
  },
  photorealistic: {
    keywords: ['photorealistic', 'photo', 'realistic', '8k photo', 'photograph', 'raw photo'],
    conflicts: ['anime', 'manga', 'cartoon', 'illustrated', 'cel shaded', '2d', 'comic', 'drawing']
  },
  comic: {
    keywords: ['comic book', 'graphic novel', 'comic style', 'ink lines'],
    conflicts: ['photorealistic', 'photo', 'realistic', '3d render', 'anime']
  },
  '3d': {
    keywords: ['3d', 'render', 'unreal engine', 'pbr textures', '3d game'],
    conflicts: ['2d', 'flat', 'cel shaded', 'hand-drawn', 'sketch']
  },
  painting: {
    keywords: ['oil painting', 'watercolor', 'painting', 'painterly'],
    conflicts: ['photorealistic', 'photo', '3d render', 'anime']
  }
};

interface StyleConflictResult {
  hasConflict: boolean;
  conflictingTerms: string[];
}

/**
 * Detect if the enhanced prompt contains style keywords that conflict with the preset
 */
function detectStyleConflicts(enhancedPrompt: string, stylePreset: string): StyleConflictResult {
  const lower = enhancedPrompt.toLowerCase();
  const presetLower = stylePreset.toLowerCase();

  // Determine the intended style category
  let intendedCategory: string | null = null;
  for (const [category, config] of Object.entries(STYLE_CATEGORIES)) {
    if (config.keywords.some(keyword => presetLower.includes(keyword))) {
      intendedCategory = category;
      break;
    }
  }

  if (!intendedCategory) {
    // No specific style detected, assume no conflict
    return { hasConflict: false, conflictingTerms: [] };
  }

  // Check for conflicting keywords in the enhanced prompt
  const conflictingTerms: string[] = [];
  const conflicts = STYLE_CATEGORIES[intendedCategory as keyof typeof STYLE_CATEGORIES].conflicts;

  for (const conflict of conflicts) {
    if (lower.includes(conflict)) {
      conflictingTerms.push(conflict);
    }
  }

  return {
    hasConflict: conflictingTerms.length > 0,
    conflictingTerms
  };
}

/**
 * Remove conflicting style terms and replace with correct style
 */
function correctStyleConflicts(
  enhancedPrompt: string,
  stylePreset: string,
  conflictingTerms: string[]
): string {
  let corrected = enhancedPrompt;

  // Remove conflicting terms
  for (const term of conflictingTerms) {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    corrected = corrected.replace(regex, '');
  }

  // Clean up extra commas and spaces
  corrected = corrected.replace(/,\s*,/g, ',').replace(/\s+,/g, ',').replace(/,\s+/g, ', ').trim();

  // Ensure the correct style is present at the beginning (after the subject)
  const translatedStyle = translateStylePreset(stylePreset);

  // If the translated style is not in the prompt, add it after the first sentence/clause
  if (!corrected.toLowerCase().includes(translatedStyle.split(',')[0].toLowerCase())) {
    // Find the first comma or period
    const firstBreak = corrected.indexOf(',');
    if (firstBreak !== -1) {
      corrected = corrected.slice(0, firstBreak) + ', ' + translatedStyle + corrected.slice(firstBreak);
    } else {
      corrected = corrected + ', ' + translatedStyle;
    }
  }

  return corrected;
}

// ============================================
// INTEGRATION HELPER
// ============================================

/**
 * Convenience function to enhance a prompt for layer generation
 * Integrates with existing buildEnforcedGenerationPrompt workflow
 */
export async function enhanceLayerPrompt(
  layerType: 'body' | 'head' | 'face' | 'hair' | 'clothing' | 'accessories',
  userInput: string,
  options: {
    form?: CharacterForm;
    stylePreset?: string;
    archetype?: string;
    hasInputImage?: boolean;
    gender?: 'male' | 'female' | 'neutral';
  } = {}
): Promise<string> {
  const enhanced = await enhancePromptWithGemini({
    userPrompt: userInput,
    layerType,
    form: options.form || 'humanoid',
    stylePreset: options.stylePreset,
    archetype: options.archetype,
    hasInputImage: options.hasInputImage,
    gender: options.gender
  });

  return enhanced.enhancedPrompt;
}

/**
 * Detect gender from prompt using simple exact matching.
 * Codex is instructed to always start enhanced prompts with "A man/woman/boy/girl"
 * so we just look for those patterns.
 */
export function detectGenderFromPrompt(prompt: string): 'male' | 'female' | 'neutral' {
  const lower = prompt.toLowerCase().trim();

  // Check for Codex's required prefix format first (most reliable)
  // Supports: "A woman", "A man", "A boy", "A girl", "An old woman", "An old man", "A young woman", etc.
  const femaleStarts = ['a woman ', 'a girl ', 'an old woman ', 'a young woman ', 'an elderly woman '];
  const maleStarts = ['a man ', 'a boy ', 'an old man ', 'a young man ', 'an elderly man '];

  if (femaleStarts.some(prefix => lower.startsWith(prefix))) {
    console.log('🔍 Female prefix detected:', lower.substring(0, 25));
    return 'female';
  }
  if (maleStarts.some(prefix => lower.startsWith(prefix))) {
    console.log('🔍 Male prefix detected:', lower.substring(0, 25));
    return 'male';
  }

  // Fallback: check for gender words anywhere with word boundaries
  // Use simple exact word matching
  const femaleWords = ['woman', 'girl', 'female', 'lady'];
  const maleWords = ['man', 'boy', 'male', 'guy'];

  const hasFemale = femaleWords.some(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(lower);
  });

  const hasMale = maleWords.some(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(lower);
  });

  if (hasFemale && !hasMale) {
    console.log('🔍 Female word detected in:', lower.substring(0, 50));
    return 'female';
  }
  if (hasMale && !hasFemale) {
    console.log('🔍 Male word detected in:', lower.substring(0, 50));
    return 'male';
  }

  // Both or neither - return neutral
  console.log('🔍 Gender unclear, returning neutral:', lower.substring(0, 50));
  return 'neutral';
}

// ============================================
// BODY ANALYSIS WITH GEMINI VISION
// ============================================

/**
 * Analyzes a body image using Codex Vision to extract:
 * 1. Immutable body characteristics (ethnicity, skin tone, gender, age)
 * 2. Initial head generation prompt
 *
 * This ensures head generations always match the body, preventing mismatches
 * like a Black body with a white head.
 */
export interface BodyAnalysisResult {
  // Part 1: Immutable rules that apply to ALL future head generations
  immutableRules: {
    ethnicity: string;           // e.g., "Caucasian", "East Asian", "African", "South Asian", "Latino", "Middle Eastern"
    skinTone: string;            // e.g., "pale", "fair", "olive", "tan", "brown", "dark brown", "ebony"
    gender: 'male' | 'female' | 'neutral';
    ageRange: string;            // e.g., "young adult (20-30)", "middle-aged (40-50)", "elderly (60+)"
    bodyType?: string;           // e.g., "athletic", "muscular", "slim", "average", "curvy"
  };

  // Part 2: Initial head generation prompt (used once, then discarded)
  initialHeadPrompt: string;     // Describes hair, facial structure, expression for first head generation

  // Natural language version of immutable rules for enforcing in future prompts
  immutableRulesText: string;    // e.g., "MUST have: dark brown skin tone, African ethnicity, female gender, young adult age"
}

export async function analyzeBodyImageForHeadGeneration(
  bodyImageUrl: string,
  characterForm: CharacterForm,
  userBodyPrompt?: string
): Promise<BodyAnalysisResult> {
  console.log('👁️ Analyzing body image with Codex Vision...');

  const prompt = characterForm === 'creature'
    ? `You are analyzing a creature character body image. Extract the following:

1. IMMUTABLE BODY CHARACTERISTICS (these MUST be maintained in all future head generations):
   - Species/Creature Type: What kind of creature is this? (e.g., werewolf, dragon, lizardfolk)
   - Body Coverage: What covers the body? (fur, scales, feathers, skin, exoskeleton)
   - Primary Color/Pattern: Main body coloration and patterns
   - Build: Body structure and build (muscular, slim, bulky, etc.)
   - Gender: If applicable (male, female, or neutral/ambiguous)

2. INITIAL HEAD PROMPT: Describe what the head should look like to match this body. Include:
   - Species-appropriate head features (snout, muzzle, beak, etc.)
   - Eye type and color
   - Ear/horn/antenna placement and style
   - Facial markings or patterns
   - Expression and demeanor
   - Head framing: "Head portrait with padding above crown and below chin for proper framing"

   CRITICAL EXCLUSIONS - The head prompt must NEVER include:
   - NO clothing, shirts, or upper body attire
   - NO accessories (jewelry, hats, helmets, etc.)
   - NO sunglasses or eyewear
   - ONLY the head/face itself with natural features

Return as JSON:
{
  "immutableRules": {
    "ethnicity": "creature species",
    "skinTone": "body coverage and color",
    "gender": "male/female/neutral",
    "ageRange": "age range",
    "bodyType": "build description"
  },
  "initialHeadPrompt": "detailed head description with framing",
  "immutableRulesText": "natural language version of rules"
}`
    : `You are analyzing a humanoid character body image. Extract the following:

1. IMMUTABLE BODY CHARACTERISTICS (these MUST be maintained in all future head generations):
   - Ethnicity: Caucasian, East Asian, African, South Asian, Latino, Middle Eastern, Mixed, etc.
   - Skin Tone: pale, fair, olive, tan, light brown, brown, dark brown, ebony
   - Gender: male, female, or neutral (based on body structure)
   - Age Range: child (5-12), teen (13-19), young adult (20-30), adult (31-45), middle-aged (46-60), elderly (60+)
   - Body Type: slim, athletic, muscular, average, curvy, heavyset

2. INITIAL HEAD PROMPT: Describe what the head/face should look like to match this body. Include:
   - Hair color, length, and style suggestions
   - Facial structure (angular, round, defined cheekbones, etc.)
   - Eye shape and color suggestions
   - Expression and demeanor
   - Head framing: "Headshot portrait with padding above crown and below chin for proper framing"

CRITICAL EXCLUSIONS - The head prompt must NEVER include:
   - NO clothing, shirts, collars, or upper body attire
   - NO accessories (jewelry, earrings, necklaces, hats, etc.)
   - NO sunglasses, eyewear, or glasses
   - ONLY the bare head/face with hair and natural facial features

CRITICAL: The ethnicity and skin tone MUST be preserved exactly in all future head generations.
For example, if this is a dark-skinned African body, ALL head generations must maintain that.

Return as JSON:
{
  "immutableRules": {
    "ethnicity": "specific ethnicity",
    "skinTone": "exact skin tone",
    "gender": "male/female/neutral",
    "ageRange": "age range",
    "bodyType": "body type"
  },
  "initialHeadPrompt": "detailed head description with framing",
  "immutableRulesText": "MUST have: [skin tone], [ethnicity] ethnicity, [gender] gender, [age] age"
}

${userBodyPrompt ? `\nUser's original body prompt: "${userBodyPrompt}"\nUse this for additional context about character intent.` : ''}`;

  try {
    const text = await requestCodexText(prompt, [bodyImageUrl]);

    // Extract JSON from response (may be wrapped in markdown code blocks)
    let jsonText = text;

    // Remove markdown code blocks if present
    const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch) {
      jsonText = codeBlockMatch[1];
    }

    // Find the first { and match it with the corresponding }
    const startIdx = jsonText.indexOf('{');
    if (startIdx === -1) {
      console.error('❌ No JSON object found in response:', text);
      throw new Error('Could not find JSON object in Codex response. Check console for full response.');
    }

    // Count braces to find the matching closing brace
    let depth = 0;
    let endIdx = -1;
    for (let i = startIdx; i < jsonText.length; i++) {
      if (jsonText[i] === '{') depth++;
      if (jsonText[i] === '}') {
        depth--;
        if (depth === 0) {
          endIdx = i;
          break;
        }
      }
    }

    if (endIdx === -1) {
      console.warn('⚠️ Truncated JSON response detected, attempting partial recovery...');
      console.warn('⚠️ Full truncated text:', text);

      // Try to salvage partial data from truncated JSON
      // Extract any values we can find
      const ethnicityMatch = jsonText.match(/"ethnicity":\s*"([^"]+)"/);
      const skinToneMatch = jsonText.match(/"skinTone":\s*"([^"]+)"/);
      const genderMatch = jsonText.match(/"gender":\s*"([^"]+)"/);
      const ageRangeMatch = jsonText.match(/"ageRange":\s*"([^"]+)"/);
      const bodyTypeMatch = jsonText.match(/"bodyType":\s*"([^"]+)"/);

      console.log('🔍 Partial extraction results:', {
        ethnicity: ethnicityMatch?.[1],
        skinTone: skinToneMatch?.[1],
        gender: genderMatch?.[1],
        ageRange: ageRangeMatch?.[1],
        bodyType: bodyTypeMatch?.[1]
      });

      // If we got at least ethnicity or skinTone, create a partial result
      if (ethnicityMatch || skinToneMatch || genderMatch) {
        console.log('🔧 Creating partial result from recovered data');
        const partialResult: BodyAnalysisResult = {
          immutableRules: {
            ethnicity: ethnicityMatch?.[1] || 'unspecified',
            skinTone: skinToneMatch?.[1] || 'unspecified',
            gender: genderMatch?.[1] || 'neutral',
            ageRange: ageRangeMatch?.[1] || 'adult',
            bodyType: bodyTypeMatch?.[1] || 'average'
          },
          initialHeadPrompt: `Headshot portrait matching ${skinToneMatch?.[1] || 'the body'} skin tone, ${genderMatch?.[1] || ''}, with padding above crown and below chin for proper framing`,
          immutableRulesText: `MUST have: ${skinToneMatch?.[1] || 'matching'} skin, ${ethnicityMatch?.[1] || 'matching'} ethnicity, ${genderMatch?.[1] || ''} gender`
        };
        console.log('✅ Recovered body analysis:', partialResult);
        return partialResult;
      }

      console.error('❌ Could not extract any usable data from truncated response');
      throw new Error('Could not find matching closing brace in JSON and failed to recover partial data. Check console for full response.');
    }

    const jsonString = jsonText.substring(startIdx, endIdx + 1);
    console.log('📋 Extracted JSON string:', jsonString);

    const result: BodyAnalysisResult = JSON.parse(jsonString);

    console.log('✅ Body analysis complete:', result);
    return result;

  } catch (error) {
    console.error('❌ Body analysis failed:', error);
    throw error;
  }
}

// ============================================
// REFERENCE IMAGE CLASSIFICATION
// ============================================

export type ReferenceImageClassification = 'head' | 'body' | 'both' | 'reference';

export interface ClassifiedReferenceImage {
  id: string;
  base64: string;
  classification: ReferenceImageClassification;
  description: string;  // Brief description of what's in the image
}

/**
 * Classify uploaded reference images using Codex Vision
 * Determines if each image is: head, body, both (full character), or reference (artwork/drawing)
 */
export async function classifyReferenceImages(
  images: Array<{ id: string; base64: string }>
): Promise<ClassifiedReferenceImage[]> {
  if (!images || images.length === 0) return [];

  console.log(`🔍 Classifying ${images.length} reference image(s)...`);

  const classifiedImages: ClassifiedReferenceImage[] = [];

  for (const image of images) {
    try {
      const prompt = `Analyze this image and classify it for character creation reference.

CLASSIFICATION OPTIONS:
1. "head" - Image shows ONLY a face/head portrait (headshot, face close-up, no body visible)
2. "body" - Image shows ONLY a body WITHOUT a clear face (body reference, pose reference, outfit on mannequin)
3. "both" - Image shows a COMPLETE character with BOTH visible face AND body (full-body character, person photo)
4. "reference" - Image is an artwork, sketch, drawing, or stylized illustration (not a photo-realistic image)

IMPORTANT DISTINCTIONS:
- Photos of real people with full body visible = "both"
- Photos of real people face/headshot only = "head"
- Anime/manga/cartoon characters = "reference" (even if showing full body)
- Sketches, drawings, concept art = "reference"
- 3D renders that look photo-realistic = classify based on what's shown (head/body/both)
- 3D renders that look stylized/artistic = "reference"

Respond with ONLY valid JSON (no markdown):
{
  "classification": "head" | "body" | "both" | "reference",
  "description": "Brief 10-15 word description of what the image shows",
  "confidence": "high" | "medium" | "low"
}`;

      const text = await requestCodexText(prompt, [image.base64]);

      // Parse JSON response
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        classifiedImages.push({
          id: image.id,
          base64: image.base64,
          classification: parsed.classification || 'reference',
          description: parsed.description || 'Reference image'
        });
        console.log(`✅ Classified image ${image.id}: ${parsed.classification} - ${parsed.description}`);
      } else {
        classifiedImages.push({
          ...image,
          classification: 'reference',
          description: 'Unable to parse classification'
        });
      }
    } catch (error) {
      console.error(`❌ Failed to classify image ${image.id}:`, error);
      classifiedImages.push({
        ...image,
        classification: 'reference',
        description: 'Classification error - using as general reference'
      });
    }
  }

  return classifiedImages;
}

/**
 * Generate reference image instructions for prompt enhancement
 * Based on classified reference images, creates specific instructions for the image generator
 * Enhanced to capture detailed anatomy and physical appearance
 */
export function generateReferenceImageInstructions(
  classifiedImages: ClassifiedReferenceImage[],
  archetype?: string,
  styleName?: string
): { headInstructions: string; bodyInstructions: string; generalInstructions: string } {
  const styleContext = styleName ? `in ${styleName} style` : archetype ? `in ${archetype} format` : '';

  let headInstructions = '';
  let bodyInstructions = '';
  let generalInstructions = '';

  for (const img of classifiedImages) {
    switch (img.classification) {
      case 'head':
        headInstructions += `REFERENCE IMAGE (Head) - MATCH WITH PIXEL-LEVEL FIDELITY ${styleContext}:
FACE STRUCTURE: Replicate exact face shape, jawline, chin, cheekbones, forehead proportions.
EYES: Match exact eye shape (round/almond/hooded/monolid), size, spacing, iris color, pupil size, lash length/thickness, under-eye details.
EYEBROWS: Copy exact brow shape, thickness, arch, color, position relative to eyes.
NOSE: Match bridge width, tip shape, nostril shape/size, nose length and projection.
LIPS: Replicate exact lip shape (cupid's bow, corners), fullness ratio (upper/lower), color.
SKIN: Match tone, texture, any freckles/moles/beauty marks in exact positions, pores, blush areas.
HAIR: Copy exact color, style, texture, length, volume, parting, how it frames the face.
EXPRESSION: Preserve the exact expression and mouth position. `;
        break;
      case 'body':
        bodyInstructions += `REFERENCE IMAGE (Body) - MATCH ANATOMY PRECISELY ${styleContext}:
BUILD: Match exact body type - slim/athletic/muscular/curvy/stocky proportions.
PROPORTIONS: Replicate shoulder width, torso length, hip width, limb proportions, height impression.
MUSCLE DEFINITION: Copy muscle tone and definition level visible in reference.
SKIN: Match skin tone, texture, any visible tattoos/scars/birthmarks in exact positions.
PHYSIQUE DETAILS: Preserve body fat distribution, bone structure visibility, overall silhouette.
POSTURE: Match natural stance and body carriage if visible. `;
        break;
      case 'both':
        headInstructions += `REFERENCE IMAGE (Full Character - Head) - MATCH FACE WITH PIXEL-LEVEL FIDELITY ${styleContext}:
FACE STRUCTURE: Replicate exact face shape, jawline, chin, cheekbones.
EYES: Match exact eye shape, size, iris color, lash details, spacing.
NOSE: Copy bridge width, tip shape, nostril shape.
LIPS: Match lip shape, fullness, cupid's bow.
SKIN: Match tone, freckles/moles/marks in exact positions.
HAIR: Copy exact color, style, length, texture, volume.
EXPRESSION: Preserve the exact expression. `;
        bodyInstructions += `REFERENCE IMAGE (Full Character - Body) - MATCH ANATOMY PRECISELY ${styleContext}:
BUILD: Replicate exact body type and proportions.
PHYSIQUE: Match muscle definition, body fat distribution, silhouette.
PROPORTIONS: Copy shoulder-to-hip ratio, torso length, limb proportions.
SKIN: Match tone and any visible marks/tattoos/scars. `;
        break;
      case 'reference':
        generalInstructions += `ARTISTIC REFERENCE: Draw inspiration from the provided artwork ${styleContext}. Capture the artistic essence while creating an original character interpretation. `;
        break;
    }
  }

  return {
    headInstructions: headInstructions.trim(),
    bodyInstructions: bodyInstructions.trim(),
    generalInstructions: generalInstructions.trim()
  };
}
